import {
  Box,
  Text,
  Flex,
  HStack,
  Badge,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { Clock, UserCog, Award, MessageCircle } from 'lucide-react';
import type { OptimizedVolunteer } from '@/utils/optimizedQueries';
import { formatDuration } from '@/utils/volunteerHours';

interface VolunteerCardProps {
  volunteer: OptimizedVolunteer;
  onManageClick?: (volunteer: OptimizedVolunteer) => void;
  /** Org admins only — opens a private thread with this volunteer. */
  onMessageClick?: (volunteer: OptimizedVolunteer) => void;
  compact?: boolean;
}

export function VolunteerCard({
  volunteer,
  onManageClick,
  onMessageClick,
  compact = false,
}: VolunteerCardProps) {
  const navigate = useNavigate();
  const profile = volunteer.profiles as { first_name?: string; last_name?: string; profile_image?: string } | undefined;
  const fullName = profile
    ? `${profile.first_name ?? ''} ${profile.last_name ?? ''}`.trim()
    : 'Volunteer';
  const initials = fullName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');
  const departments = volunteer.departments ?? [];
  const hours = volunteer.hours ?? 0;
  const certificateCount = volunteer.certificateCount ?? 0;

  const avatarSize = compact ? '36px' : '44px';
  const iconSize = compact ? 12 : 13;
  const buttonSize = compact ? '34px' : '38px';
  const cogSize = compact ? 14 : 18;

  const content = (
    <Flex alignItems="flex-start" gap="3">
      <Box
        as="button"
        w={avatarSize}
        h={avatarSize}
        borderRadius="full"
        overflow="hidden"
        bg="volyx.lightGray"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        border="none"
        cursor="pointer"
        p="0"
        onClick={() => navigate(`/profile?id=${volunteer.volunteer_id}`)}
      >
        {profile?.profile_image ? (
          <img
            src={profile.profile_image}
            alt={fullName}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Text fontSize="xs" fontWeight="semibold" color="volyx.textSecondary">
            {initials}
          </Text>
        )}
      </Box>
      <Box flex="1" minW="0">
        <Flex align="center" gap="2" minW="0">
          <Box
            as="button"
            textAlign="left"
            bg="transparent"
            border="none"
            cursor="pointer"
            p="0"
            w="auto"
            minW="0"
            onClick={() => navigate(`/profile?id=${volunteer.volunteer_id}`)}
          >
            <Text fontSize="sm" fontWeight={compact ? 'medium' : 'semibold'} color="volyx.primary" truncate m="0" _hover={{ textDecoration: 'underline' }}>
              {fullName}
            </Text>
          </Box>
          {volunteer.orgMembershipRole === 'admin' && (
            <Badge
              px="2"
              py="0.5"
              borderRadius="full"
              bg="volyx.primaryLight"
              borderWidth="1.5px"
              borderColor="#007AFF"
              fontSize="10px"
              fontWeight="semibold"
              color="#007AFF"
              flexShrink={0}
            >
              Admin
            </Badge>
          )}
        </Flex>
        {certificateCount > 0 && (
          <HStack mt={compact ? '1.5' : '1'} gap="1">
            <Award size={iconSize} style={{ color: 'var(--chakra-colors-volyx-primary)' }} />
            <Text fontSize="xs" color="volyx.textSecondary" m="0">{certificateCount} cert{certificateCount !== 1 ? 's' : ''}</Text>
          </HStack>
        )}
        {!compact && departments.length > 0 && (
          <Text fontSize="xs" color="volyx.textSecondary" truncate m="0" mt="0.5">
            {departments.join(', ')}
          </Text>
        )}
      </Box>
      <HStack gap="3" flexShrink={0} align="center">
        <HStack gap="1">
          <Clock size={iconSize} style={{ color: 'var(--chakra-colors-volyx-textSecondary)' }} />
          <Text fontSize="xs" color="volyx.textSecondary" m="0" whiteSpace="nowrap">{formatDuration(hours)}</Text>
        </HStack>
        {onMessageClick && (
          <Box
            as="button"
            w={buttonSize}
            h={buttonSize}
            borderRadius="full"
            // Soft blue against the solid navy of Manage: same shape and size, so the two
            // read as a pair, but clearly the lighter-weight action of the two. Matches the
            // primaryLight/action pairing already used by the Admin badge above.
            bg="volyx.primaryLight"
            borderWidth="1.5px"
            borderStyle="solid"
            borderColor="volyx.action"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            flexShrink={0}
            transition="all 0.2s"
            aria-label={`Message ${fullName}`}
            _hover={{ bg: 'volyx.action', transform: 'scale(1.05)' }}
            _active={{ transform: 'scale(0.95)' }}
            css={{ '&:hover svg': { color: '#FFFFFF' } }}
            onClick={(e) => {
              e.stopPropagation();
              onMessageClick(volunteer);
            }}
          >
            {/* style, not the `color` prop: lucide maps `color` to the SVG stroke
                ATTRIBUTE, which does not accept var(). Sibling icons here do the same. */}
            <MessageCircle
              size={cogSize}
              style={{ color: 'var(--chakra-colors-volyx-action)', transition: 'color 0.2s' }}
            />
          </Box>
        )}
        {onManageClick && (
          <Box
            as="button"
            w={buttonSize}
            h={buttonSize}
            borderRadius="full"
            bg="volyx.primary"
            display="flex"
            alignItems="center"
            justifyContent="center"
            border="none"
            cursor="pointer"
            flexShrink={0}
            transition="all 0.2s"
            aria-label="Manage volunteer"
            _hover={{ opacity: 0.85, transform: 'scale(1.05)' }}
            onClick={(e) => {
              e.stopPropagation();
              onManageClick(volunteer);
            }}
          >
            <UserCog size={cogSize} color="white" />
          </Box>
        )}
      </HStack>
    </Flex>
  );

  if (compact) {
    return (
      <Box _hover={{ bg: 'volyx.lightGray' }} borderRadius="lg" py="2" px="2">
        {content}
      </Box>
    );
  }

  return (
    <Box
      bg="volyx.bg"
      borderRadius="xl"
      boxShadow="0 6px 18px rgba(0,0,0,0.12)"
      p="4"
      transition="box-shadow 0.2s"
      _hover={{ boxShadow: '0 8px 20px rgba(0,0,0,0.12)' }}
    >
      {content}
    </Box>
  );
}
